import L from 'leaflet';
import { MapLayer, withLeaflet } from 'react-leaflet';
import 'prop-types';
import React from 'react';

L.DomUtil = L.extend(L.DomUtil, {
	getMatrixString: function getMatrixString(m) {
		var is3d = L.Browser.webkit3d || L.Browser.gecko3d || L.Browser.ie3d,


		/* 
      * Since matrix3d takes a 4*4 matrix, we add in an empty row and column, which act as the identity on the z-axis.
      * See:
      *     http://franklinta.com/2014/09/08/computing-css-matrix3d-transforms/
      *     https://developer.mozilla.org/en-US/docs/Web/CSS/transform-function#M.C3.B6bius'_homogeneous_coordinates_in_projective_geometry
      */
		matrix = [m[0], m[3], 0, m[6], m[1], m[4], 0, m[7], 0, 0, 1, 0, m[2], m[5], 0, m[8]],
		    str = is3d ? 'matrix3d(' + matrix.join(',') + ')' : '';

		if (!is3d) {
			console.log('Your browser must support 3D CSS transforms in order to use DistortableImageOverlay.');
		}

		return str;
	},

	getRotateString: function getRotateString(angle, units) {
		var is3d = L.Browser.webkit3d || L.Browser.gecko3d || L.Browser.ie3d,
		    open = 'rotate' + (is3d ? '3d' : '') + '(',
		    rotateString = (is3d ? '0, 0, 1, ' : '') + angle + units;

		return open + rotateString + ')';
	}
});

L.Map.include({
	_newLayerPointToLatLng: function _newLayerPointToLatLng(point, newZoom, newCenter) {
		var topLeft = L.Map.prototype._getNewTopLeftPoint.call(this, newCenter, newZoom).add(L.Map.prototype._getMapPanePos.call(this));
		return this.unproject(point.add(topLeft), newZoom);
	}
});
L.MatrixUtil = {

	// Compute the adjugate of m
	adj: function adj(m) {
		return [m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4], m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5], m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3]];
	},

	// multiply two 3*3 matrices
	multmm: function multmm(a, b) {
		var c = [],
		    i;

		for (i = 0; i < 3; i++) {
			for (var j = 0; j < 3; j++) {
				var cij = 0;
				for (var k = 0; k < 3; k++) {
					cij += a[3 * i + k] * b[3 * k + j];
				}
				c[3 * i + j] = cij;
			}
		}
		return c;
	},

	// multiply a 3*3 matrix and a 3-vector
	multmv: function multmv(m, v) {
		return [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
	},

	// multiply a scalar and a 3*3 matrix
	multsm: function multsm(s, m) {
		var matrix = [];

		for (var i = 0, l = m.length; i < l; i++) {
			matrix.push(s * m[i]);
		}

		return matrix;
	},

	basisToPoints: function basisToPoints(x1, y1, x2, y2, x3, y3, x4, y4) {
		var m = [x1, x2, x3, y1, y2, y3, 1, 1, 1],
		    v = L.MatrixUtil.multmv(L.MatrixUtil.adj(m), [x4, y4, 1]);

		return L.MatrixUtil.multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
	},

	project: function project(m, x, y) {
		var v = L.MatrixUtil.multmv(m, [x, y, 1]);
		return [v[0] / v[2], v[1] / v[2]];
	},

	general2DProjection: function general2DProjection(x1s, y1s, x1d, y1d, x2s, y2s, x2d, y2d, x3s, y3s, x3d, y3d, x4s, y4s, x4d, y4d) {
		var s = L.MatrixUtil.basisToPoints(x1s, y1s, x2s, y2s, x3s, y3s, x4s, y4s),
		    d = L.MatrixUtil.basisToPoints(x1d, y1d, x2d, y2d, x3d, y3d, x4d, y4d),
		    m = L.MatrixUtil.multmm(d, L.MatrixUtil.adj(s));

		/* 
   *	Normalize to the unique matrix with m[8] == 1. 
   * 	See: http://franklinta.com/2014/09/08/computing-css-matrix3d-transforms/
   */
		return L.MatrixUtil.multsm(1 / m[8], m);
	}
};
L.EditHandle = L.Marker.extend({
	initialize: function initialize(overlay, corner, options) {
		var markerOptions,
		    latlng = overlay._corners[corner];

		L.setOptions(this, options);

		this._handled = overlay;
		this._corner = corner;

		markerOptions = {
			draggable: true,
			zIndexOffset: 10
		};

		if (options && options.hasOwnProperty('draggable')) {
			markerOptions.draggable = options.draggable;
		}

		L.Marker.prototype.initialize.call(this, latlng, markerOptions);
	},

	onAdd: function onAdd(map) {
		L.Marker.prototype.onAdd.call(this, map);
		this._bindListeners();

		this.updateHandle();
	},

	onRemove: function onRemove(map) {
		this._unbindListeners();
		L.Marker.prototype.onRemove.call(this, map);
	},

	_onHandleDragStart: function _onHandleDragStart() {
		this._handled.fire('editstart');
	},

	_onHandleDragEnd: function _onHandleDragEnd() {
		this._fireEdit();
	},

	_fireEdit: function _fireEdit() {
		this._handled.edited = true;
		this._handled.fire('edit');
	},

	_bindListeners: function _bindListeners() {
		this.on({
			'dragstart': this._onHandleDragStart,
			'drag': this._onHandleDrag,
			'dragend': this._onHandleDragEnd
		}, this);

		this._handled._map.on('zoomend', this.updateHandle, this);

		this._handled.on('update', this.updateHandle, this);
	},

	_unbindListeners: function _unbindListeners() {
		this.off({
			'dragstart': this._onHandleDragStart,
			'drag': this._onHandleDrag,
			'dragend': this._onHandleDragEnd
		}, this);

		this._handled._map.off('zoomend', this.updateHandle, this);
		this._handled.off('update', this.updateHandle, this);
	}
});

L.LockHandle = L.EditHandle.extend({
	options: {
		TYPE: 'lock',
		icon: new L.Icon({
			iconUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAD8SURBVO3BPU7CYAAA0AdfjIcQlRCQBG7C3gk2uIPG2RC3Dk16Gz0FTO1WZs/gwGCMP/2+xsSl7+n1er1Iz9LtRQjaPeMeO+TinLDCJV78YqjdA04YodKuxhUaPGoRxMmxwRQZSt87Yo4KExGCeAUyLLFB4bMacxywEClIU2KDKXbInTUYo8JCgoFuGoxQO5uiwY1EA91VmDqrcKeDoX8WdNNgjApvmGGLXKIgXY0xGkxQYItrrFFIEKQ5Yo4KEx9yrDFDhlKkIF6NOQ5Y+KpAhiXWKEQI4pxwiwoLPyuxwQw75FoE7fZYocFEuwI7jHCBV39gL92TXq/Xi/AOcmczZmaIMScAAAAASUVORK5CYII=',
			iconSize: [32, 32],
			iconAnchor: [16, 16] })
	},

	/* cannot be dragged */
	_onHandleDrag: function _onHandleDrag() {},

	updateHandle: function updateHandle() {
		this.setLatLng(this._handled._corners[this._corner]);
	}

});

L.DistortHandle = L.EditHandle.extend({
	options: {
		TYPE: 'distort',
		icon: new L.Icon({
			iconUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAChSURBVO3BIU4DURgGwNkvL2B6AkQTLBqP4QCoSm7DDXoBLBZHDbfgICAIZjEV3YTn9uVHdMZZtcnCfI13bIzxg0emg6Nm6QVbYz3jylEsXRrvwommb49X67jFkz80fR9Mb1YxTzqiWBSLYlEsikWxKBbFolgUi2JRLIpFsSgWxaJY03fHHOu40dH07bAzWCx9Ge/TiWbpHgdsjPGNB2f/yS+7xRCyiiZPJQAAAABJRU5ErkJggg==',
			iconSize: [32, 32],
			iconAnchor: [16, 16] })
	},

	updateHandle: function updateHandle() {
		this.setLatLng(this._handled._corners[this._corner]);
	},

	_onHandleDrag: function _onHandleDrag() {
		this._handled._updateCorner(this._corner, this.getLatLng());

		this._handled.fire('update');
	}
});

L.RotateHandle = L.EditHandle.extend({
	options: {
		TYPE: 'rotate',
		icon: new L.Icon({
			iconUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAHiSURBVMXBa3HbShgA0PMp/1sCCo8oCEpgTaCXgIXAJiDzyCJoAUTm4UVQAns1Y8+snWnTvJyeE16hkjDgDrfoNTMKcpC9UPiLSo8JyetkjEHxjPCMyoS199kFoz8Iv1HpMaN3qWDCHoegOKkkRwnJpRmroHgiPFEZ8IBekzEGxQtUEhKSS/fB7Ew4U+lxcGkVZG9QWWPSFAxBcdK59KApuA+yNwp2uEdx1GN25sZJZULSfAtm77SlbNjju6MvG75u+WHRWVR6rDVjMPsgwYyVZl3pLTpHkyYHOx8syMiayaJzlDTZ9YyaZNFVkiYH2ZUEBcVJJXVImuz6Js3Qofe59pq7DoOTILu+g+a288mCouk7/1iH4qTS+2QdDppbV1ZJmrnDXnPnc5UOs2Z0fUmTuyBr+krvSioJyUmQO0dZM7mepMkWnaNRkyrJB6uskTSjxY3Fll8bvmJwlDb83FJ8gMqAB80uyBY3Trb82PAfvjj6vuHnluIdKgMeNXOwctK5NKBoHitrb1RJeHRp5Ux4ojLg0aWMHGQvUOkxIWkKVsHsTPiNSo8HDC5lZIsgO6n0uMUdRvQuFQxB8UR4RmXC2vvsgtEfhL+o9JiQvE7GGBTPCK9QSUjoMWgKDthjDrIX+h/k0I7gth6N5gAAAABJRU5ErkJggg==',
			iconSize: [32, 32],
			iconAnchor: [16, 16] })
	},

	_onHandleDrag: function _onHandleDrag() {
		var overlay = this._handled,
		    formerLatLng = this._handled._corners[this._corner],
		    newLatLng = this.getLatLng(),
		    angle = this._calculateAngle(formerLatLng, newLatLng);
		//scale = this._calculateScalingFactor(formerLatLng, newLatLng);

		overlay.editing._rotateBy(angle);
		//overlay.editing._scaleBy(scale);

		overlay.fire('update');
	},

	updateHandle: function updateHandle() {
		this.setLatLng(this._handled._corners[this._corner]);
	},

	/* Takes two latlngs and calculates the angle between them. */
	_calculateAngle: function _calculateAngle(latlngA, latlngB) {
		var map = this._handled._map,
		    centerPoint = map.latLngToLayerPoint(this._handled.getCenter()),
		    formerPoint = map.latLngToLayerPoint(latlngA),
		    newPoint = map.latLngToLayerPoint(latlngB),
		    initialAngle = Math.atan2(centerPoint.y - formerPoint.y, centerPoint.x - formerPoint.x),
		    newAngle = Math.atan2(centerPoint.y - newPoint.y, centerPoint.x - newPoint.x);

		return newAngle - initialAngle;
	},

	/* Takes two latlngs and calculates the scaling difference. */
	_calculateScalingFactor: function _calculateScalingFactor(latlngA, latlngB) {
		var map = this._handled._map,
		    centerPoint = map.latLngToLayerPoint(this._handled.getCenter()),
		    formerPoint = map.latLngToLayerPoint(latlngA),
		    newPoint = map.latLngToLayerPoint(latlngB),
		    formerRadiusSquared = this._d2(centerPoint, formerPoint),
		    newRadiusSquared = this._d2(centerPoint, newPoint);

		return Math.sqrt(newRadiusSquared / formerRadiusSquared);
	},

	/* Distance between two points in cartesian space, squared (distance formula). */
	_d2: function _d2(a, b) {
		var dx = a.x - b.x,
		    dy = a.y - b.y;

		return Math.pow(dx, 2) + Math.pow(dy, 2);
	}
});

// Start Scale
L.ScaleHandle = L.EditHandle.extend({
	options: {
		TYPE: 'scale',
		icon: new L.Icon({
			iconUrl: 'data:image/svg+xml;base64,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',
			iconSize: [32, 32],
			iconAnchor: [16, 16] })
	},

	_onHandleDrag: function _onHandleDrag() {
		var overlay = this._handled,
		    formerLatLng = this._handled._corners[this._corner],
		    newLatLng = this.getLatLng(),
		    angle = this._calculateAngle(formerLatLng, newLatLng),
		    scale = this._calculateScalingFactor(formerLatLng, newLatLng);

		//overlay.editing._rotateBy(angle);
		overlay.editing._scaleBy(scale);

		overlay.fire('update');
	},

	updateHandle: function updateHandle() {
		this.setLatLng(this._handled._corners[this._corner]);
	},

	/* Takes two latlngs and calculates the angle between them. */
	_calculateAngle: function _calculateAngle(latlngA, latlngB) {
		var map = this._handled._map,
		    centerPoint = map.latLngToLayerPoint(this._handled.getCenter()),
		    formerPoint = map.latLngToLayerPoint(latlngA),
		    newPoint = map.latLngToLayerPoint(latlngB),
		    initialAngle = Math.atan2(centerPoint.y - formerPoint.y, centerPoint.x - formerPoint.x),
		    newAngle = Math.atan2(centerPoint.y - newPoint.y, centerPoint.x - newPoint.x);

		return newAngle - initialAngle;
	},

	/* Takes two latlngs and calculates the scaling difference. */
	_calculateScalingFactor: function _calculateScalingFactor(latlngA, latlngB) {
		var map = this._handled._map,
		    centerPoint = map.latLngToLayerPoint(this._handled.getCenter()),
		    formerPoint = map.latLngToLayerPoint(latlngA),
		    newPoint = map.latLngToLayerPoint(latlngB),
		    formerRadiusSquared = this._d2(centerPoint, formerPoint),
		    newRadiusSquared = this._d2(centerPoint, newPoint);

		return Math.sqrt(newRadiusSquared / formerRadiusSquared);
	},

	/* Distance between two points in cartesian space, squared (distance formula). */
	_d2: function _d2(a, b) {
		var dx = a.x - b.x,
		    dy = a.y - b.y;

		return Math.pow(dx, 2) + Math.pow(dy, 2);
	}
});

// End Scale


L.DistortableImageOverlay = L.ImageOverlay.extend({
	include: L.Mixin.Events,

	options: {
		alt: '',
		height: 200,
		crossOrigin: true
	},

	initialize: function initialize(url, options) {
		this._url = url;
		this._rotation = this.options.rotation;

		L.setOptions(this, options);
	},

	onAdd: function onAdd(map) {
		/* Copied from L.ImageOverlay */
		this._map = map;

		if (!this._image) {
			this._initImage();
		}
		if (!this._events) {
			this._initEvents();
		}

		map._panes.overlayPane.appendChild(this._image);

		map.on('viewreset', this._reset, this);
		/* End copied from L.ImageOverlay */

		/* Use provided corners if available */
		if (this.options.corners) {
			this._corners = this.options.corners;
			if (map.options.zoomAnimation && L.Browser.any3d) {
				map.on('zoomanim', this._animateZoom, this);
			}

			/* This reset happens before image load; it allows 
    * us to place the image on the map earlier with 
    * "guessed" dimensions. */
			this._reset();
		}

		/* Have to wait for the image to load because 
   * we need to access its width and height. */
		L.DomEvent.on(this._image, 'load', function () {
			this._initImageDimensions();
			this._reset();
			/* Initialize default corners if not already set */
			if (!this._corners) {
				if (map.options.zoomAnimation && L.Browser.any3d) {
					map.on('zoomanim', this._animateZoom, this);
				}
			}
		}, this);

		this.fire('add');
	},

	onRemove: function onRemove(map) {
		this.fire('remove');

		L.ImageOverlay.prototype.onRemove.call(this, map);
	},

	_initImage: function _initImage() {
		L.ImageOverlay.prototype._initImage.call(this);

		L.extend(this._image, {
			alt: this.options.alt
		});
	},

	_initImageDimensions: function _initImageDimensions() {
		var map = this._map,
		    originalImageWidth = L.DomUtil.getStyle(this._image, 'width'),
		    originalImageHeight = L.DomUtil.getStyle(this._image, 'height'),
		    aspectRatio = parseInt(originalImageWidth) / parseInt(originalImageHeight),
		    imageHeight = this.options.height,
		    imageWidth = parseInt(aspectRatio * imageHeight),
		    center = map.latLngToContainerPoint(map.getCenter()),
		    offset = new L.Point(imageWidth, imageHeight).divideBy(2);

		if (this.options.corners) {
			this._corners = this.options.corners;
		} else {
			this._corners = [map.containerPointToLatLng(center.subtract(offset)), map.containerPointToLatLng(center.add(new L.Point(offset.x, -offset.y))), map.containerPointToLatLng(center.add(new L.Point(-offset.x, offset.y))), map.containerPointToLatLng(center.add(offset))];
		}
	},

	_initEvents: function _initEvents() {
		this._events = ['click'];

		for (var i = 0, l = this._events.length; i < l; i++) {
			L.DomEvent.on(this._image, this._events[i], this._fireMouseEvent, this);
		}
	},

	/* See src/layer/vector/Path.SVG.js in the Leaflet source. */
	_fireMouseEvent: function _fireMouseEvent(event) {
		if (!this.hasEventListeners(event.type)) {
			return;
		}

		var map = this._map,
		    containerPoint = map.mouseEventToContainerPoint(event),
		    layerPoint = map.containerPointToLayerPoint(containerPoint),
		    latlng = map.layerPointToLatLng(layerPoint);

		this.fire(event.type, {
			latlng: latlng,
			layerPoint: layerPoint,
			containerPoint: containerPoint,
			originalEvent: event
		});
	},

	_updateCorner: function _updateCorner(corner, latlng) {
		this._corners[corner] = latlng;
		this._reset();
	},

	/* Copied from Leaflet v0.7 https://github.com/Leaflet/Leaflet/blob/66282f14bcb180ec87d9818d9f3c9f75afd01b30/src/dom/DomUtil.js#L189-L199 */
	/* since L.DomUtil.getTranslateString() is deprecated in Leaflet v1.0 */
	_getTranslateString: function _getTranslateString(point) {
		// on WebKit browsers (Chrome/Safari/iOS Safari/Android) using translate3d instead of translate
		// makes animation smoother as it ensures HW accel is used. Firefox 13 doesn't care
		// (same speed either way), Opera 12 doesn't support translate3d

		var is3d = L.Browser.webkit3d,
		    open = 'translate' + (is3d ? '3d' : '') + '(',
		    close = (is3d ? ',0' : '') + ')';

		return open + point.x + 'px,' + point.y + 'px' + close;
	},

	_reset: function _reset() {
		var map = this._map,
		    image = this._image,
		    latLngToLayerPoint = L.bind(map.latLngToLayerPoint, map),
		    transformMatrix = this._calculateProjectiveTransform(latLngToLayerPoint),
		    topLeft = latLngToLayerPoint(this._corners[0]),
		    warp = L.DomUtil.getMatrixString(transformMatrix),
		    translation = this._getTranslateString(topLeft);

		/* See L.DomUtil.setPosition. Mainly for the purposes of L.Draggable. */
		image._leaflet_pos = topLeft;

		image.style[L.DomUtil.TRANSFORM] = [translation, warp].join(' ');

		/* Set origin to the upper-left corner rather than the center of the image, which is the default. */
		image.style[L.DomUtil.TRANSFORM + '-origin'] = "0 0 0";
	},

	/*
  * Calculates the transform string that will be correct *at the end* of zooming.
  * Leaflet then generates a CSS3 animation between the current transform and 
  *		 future transform which makes the transition appear smooth.
  */
	_animateZoom: function _animateZoom(event) {
		var map = this._map,
		    image = this._image,
		    latLngToNewLayerPoint = function latLngToNewLayerPoint(latlng) {
			return map._latLngToNewLayerPoint(latlng, event.zoom, event.center);
		},
		    transformMatrix = this._calculateProjectiveTransform(latLngToNewLayerPoint),
		    topLeft = latLngToNewLayerPoint(this._corners[0]),
		    warp = L.DomUtil.getMatrixString(transformMatrix),
		    translation = this._getTranslateString(topLeft);

		/* See L.DomUtil.setPosition. Mainly for the purposes of L.Draggable. */
		image._leaflet_pos = topLeft;

		if (!L.Browser.gecko) {
			image.style[L.DomUtil.TRANSFORM] = [translation, warp].join(' ');
		}
	},

	getCorners: function getCorners() {
		return this._corners;
	},

	/*
  * Calculates the centroid of the image.
  *		 See http://stackoverflow.com/questions/6149175/logical-question-given-corners-find-center-of-quadrilateral
  */
	getCenter: function getCenter(ll2c, c2ll) {
		var map = this._map,
		    latLngToCartesian = ll2c ? ll2c : map.latLngToLayerPoint,
		    cartesianToLatLng = c2ll ? c2ll : map.layerPointToLatLng,
		    nw = latLngToCartesian.call(map, this._corners[0]),
		    ne = latLngToCartesian.call(map, this._corners[1]),
		    se = latLngToCartesian.call(map, this._corners[2]),
		    sw = latLngToCartesian.call(map, this._corners[3]),
		    nmid = nw.add(ne.subtract(nw).divideBy(2)),
		    smid = sw.add(se.subtract(sw).divideBy(2));

		return cartesianToLatLng.call(map, nmid.add(smid.subtract(nmid).divideBy(2)));
	},

	_calculateProjectiveTransform: function _calculateProjectiveTransform(latLngToCartesian) {
		/* Setting reasonable but made-up image defaults 
   * allow us to place images on the map before 
   * they've finished downloading. */
		var offset = latLngToCartesian(this._corners[0]),
		    w = this._image.offsetWidth || 500,
		    h = this._image.offsetHeight || 375,
		    c = [],
		    j;
		/* Convert corners to container points (i.e. cartesian coordinates). */
		for (j = 0; j < this._corners.length; j++) {
			c.push(latLngToCartesian(this._corners[j])._subtract(offset));
		}

		/*
   * This matrix describes the action of the CSS transform on each corner of the image.
   * It maps from the coordinate system centered at the upper left corner of the image
   *		 to the region bounded by the latlngs in this._corners.
   * For example:
   *		 0, 0, c[0].x, c[0].y
   *		 says that the upper-left corner of the image maps to the first latlng in this._corners.
   */
		return L.MatrixUtil.general2DProjection(0, 0, c[0].x, c[0].y, w, 0, c[1].x, c[1].y, 0, h, c[2].x, c[2].y, w, h, c[3].x, c[3].y);
	}
});

L.DistortableImage = L.DistortableImage || {};

L.DistortableImage.Edit = L.Handler.extend({
	options: {
		opacity: 0.7,
		outline: '1px solid red',
		keymap: {
			68: '_toggleRotateDistort', // d
			73: '_toggleIsolate', // i
			76: '_toggleLock', // l
			79: '_toggleOutline', // o
			82: '_toggleRotateDistort', // r
			84: '_toggleTransparency' // t
		}
	},

	initialize: function initialize(overlay) {
		this._overlay = overlay;

		/* Interaction modes. */
		this._mode = this._overlay.options.mode || 'distort';
		this._transparent = false;
		this._outlined = false;
	},

	/* Run on image seletion. */
	addHooks: function addHooks() {
		var overlay = this._overlay,
		    map = overlay._map,
		    i;

		this._lockHandles = new L.LayerGroup();
		for (i = 0; i < 4; i++) {
			this._lockHandles.addLayer(new L.LockHandle(overlay, i, { draggable: false }));
		}

		this._distortHandles = new L.LayerGroup();
		for (i = 0; i < 4; i++) {
			this._distortHandles.addLayer(new L.DistortHandle(overlay, i));
		}

		this._rotateHandles = new L.LayerGroup();
		for (i = 0; i < 4; i++) {
			this._rotateHandles.addLayer(new L.RotateHandle(overlay, i));
		}

		this._scaleHandles = new L.LayerGroup();

		var topLeftScaleHandle = new L.ScaleHandle(overlay, 0);
		console.log(topLeftScaleHandle);
		console.log(topLeftScaleHandle.options);
		console.log(topLeftScaleHandle._icon);

		//topLeftScaleHandle.options.icon.style[L.DomUtil.TRANSFORM] += ' rotateZ(90deg)';
		this._scaleHandles.addLayer(topLeftScaleHandle);

		//this._scaleHandles.addLayer( new L.ScaleHandle(overlay, 0));
		this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 1));
		this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 2));
		this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 3));

		this._handles = {
			'lock': this._lockHandles,
			'distort': this._distortHandles,
			'rotate': this._rotateHandles,
			'scale': this._scaleHandles
		};

		if (this._mode === 'lock') {
			map.addLayer(this._lockHandles);
		} else {
			this._mode = 'distort';
			map.addLayer(this._distortHandles);
			this._enableDragging();
		}

		//overlay.on('click', this._showToolbar, this);
		L.DomEvent.on(overlay._image, 'click', this._showToolbar, this);

		/* Enable hotkeys. */
		L.DomEvent.on(window, 'keydown', this._onKeyDown, this);

		overlay.fire('select');
	},

	/* Run on image deseletion. */
	removeHooks: function removeHooks() {
		var overlay = this._overlay,
		    map = overlay._map;

		// L.DomEvent.off(window, 'keydown', this._onKeyDown, this);

		L.DomEvent.off(overlay._image, 'click', this._showToolbar, this);

		// First, check if dragging exists;
		// it may be off due to locking
		if (this.dragging) {
			this.dragging.disable();
		}
		delete this.dragging;

		map.removeLayer(this._handles[this._mode]);

		/* Disable hotkeys. */
		L.DomEvent.off(window, 'keydown', this._onKeyDown, this);

		overlay.fire('deselect');
	},

	_rotateBy: function _rotateBy(angle) {
		var overlay = this._overlay,
		    map = overlay._map,
		    center = map.latLngToLayerPoint(overlay.getCenter()),
		    i,
		    p,
		    q;

		for (i = 0; i < 4; i++) {
			p = map.latLngToLayerPoint(overlay._corners[i]).subtract(center);
			q = new L.Point(Math.cos(angle) * p.x - Math.sin(angle) * p.y, Math.sin(angle) * p.x + Math.cos(angle) * p.y);
			overlay._corners[i] = map.layerPointToLatLng(q.add(center));
		}

		overlay._reset();
	},

	_scaleBy: function _scaleBy(scale) {
		var overlay = this._overlay,
		    map = overlay._map,
		    center = map.latLngToLayerPoint(overlay.getCenter()),
		    i,
		    p;

		for (i = 0; i < 4; i++) {
			p = map.latLngToLayerPoint(overlay._corners[i]).subtract(center).multiplyBy(scale).add(center);
			overlay._corners[i] = map.layerPointToLatLng(p);
		}

		overlay._reset();
	},

	_enableDragging: function _enableDragging() {
		var overlay = this._overlay,
		    map = overlay._map;

		this.dragging = new L.Draggable(overlay._image);
		this.dragging.enable();

		/* Hide toolbars while dragging; click will re-show it */
		this.dragging.on('dragstart', this._hideToolbar, this);

		/* 
   * Adjust default behavior of L.Draggable.
   * By default, L.Draggable overwrites the CSS3 distort transform 
   * that we want when it calls L.DomUtil.setPosition.
   */
		this.dragging._updatePosition = function () {
			var delta = this._newPos.subtract(map.latLngToLayerPoint(overlay._corners[0])),
			    currentPoint,
			    i;

			this.fire('predrag');

			for (i = 0; i < 4; i++) {
				currentPoint = map.latLngToLayerPoint(overlay._corners[i]);
				overlay._corners[i] = map.layerPointToLatLng(currentPoint.add(delta));
			}
			overlay._reset();
			overlay.fire('update');

			this.fire('drag');
		};
	},

	_onKeyDown: function _onKeyDown(event) {
		var keymap = this.options.keymap,
		    handlerName = keymap[event.which];

		if (handlerName !== undefined) {
			this[handlerName].call(this);
		}
	},

	_toggleRotateDistort: function _toggleRotateDistort() {
		var map = this._overlay._map;

		map.removeLayer(this._handles[this._mode]);

		/* Switch mode. */
		if (this._mode === 'rotate') {
			this._mode = 'distort';
		} else {
			this._mode = 'rotate';
		}

		map.addLayer(this._handles[this._mode]);
	},

	_toggleScale: function _toggleScale() {
		var map = this._overlay._map;

		map.removeLayer(this._handles[this._mode]);

		if (this._mode === 'scale') {
			this._mode = 'distort';
		} else {
			this._mode = 'scale';
		}

		map.addLayer(this._handles[this._mode]);
	},

	_toggleTransparency: function _toggleTransparency() {
		var image = this._overlay._image,
		    opacity;

		this._transparent = !this._transparent;
		opacity = this._transparent ? this.options.opacity : 1;

		L.DomUtil.setOpacity(image, opacity);
		image.setAttribute('opacity', opacity);
	},

	_toggleOutline: function _toggleOutline() {
		var image = this._overlay._image,
		    opacity,
		    outline;

		this._outlined = !this._outlined;
		opacity = this._outlined ? this.options.opacity / 2 : 1;
		outline = this._outlined ? this.options.outline : 'none';

		L.DomUtil.setOpacity(image, opacity);
		image.setAttribute('opacity', opacity);

		image.style.outline = outline;
	},

	_toggleLock: function _toggleLock() {
		var map = this._overlay._map;

		map.removeLayer(this._handles[this._mode]);
		/* Switch mode. */
		if (this._mode === 'lock') {
			this._mode = 'distort';
			this._enableDragging();
		} else {
			this._mode = 'lock';
			if (this.dragging) {
				this.dragging.disable();
			}
			delete this.dragging;
		}

		map.addLayer(this._handles[this._mode]);
	},

	_hideToolbar: function _hideToolbar() {
		var map = this._overlay._map;
		if (this.toolbar) {
			map.removeLayer(this.toolbar);
			this.toolbar = false;
		}
	},

	_showToolbar: function _showToolbar(event) {
		var overlay = this._overlay,
		    target = event.target,
		    map = overlay._map;

		/* Ensure that there is only ever one toolbar attached to each image. */
		this._hideToolbar();
		var point;
		if (event.containerPoint) {
			point = event.containerPoint;
		} else {
			point = target._leaflet_pos;
		}
		var raised_point = map.containerPointToLatLng(new L.Point(point.x, point.y - 20));
		raised_point.lng = overlay.getCenter().lng;
		this.toolbar = new L.DistortableImage.EditToolbar(raised_point).addTo(map, overlay);
		overlay.fire('toolbar:created');

		L.DomEvent.stopPropagation(event);
	},

	toggleIsolate: function toggleIsolate() {
		// this.isolated = !this.isolated;
		// if (this.isolated) {
		// 	$.each($L.images,function(i,img) {
		// 		img.hidden = false;
		// 		img.setOpacity(1);
		// 	});
		// } else {
		// 	$.each($L.images,function(i,img) {
		// 		img.hidden = true;
		// 		img.setOpacity(0);
		// 	});
		// }
		// this.hidden = false;
		// this.setOpacity(1);
	}

});

L.DistortableImageOverlay.addInitHook(function () {
	this.editing = new L.DistortableImage.Edit(this);

	if (this.options.editable) {
		L.DomEvent.on(this._image, 'load', this.editing.enable, this.editing);
	}

	this.on('remove', function () {
		if (this.editing) {
			this.editing.disable();
		}
	});
});

/**
 * Leaflet vector features drag functionality
 * @author Alexander Milevski <info@w8r.name>
 * @preserve
 */

/**
 * Matrix transform path for SVG/VML
 * Renderer-independent
 */
L.Path.include({

  /**
   * Applies matrix transformation to SVG
   * @param {Array.<Number>?} matrix
   */
  _transform: function _transform(matrix) {
    if (this._renderer) {
      if (matrix) {
        this._renderer.transformPath(this, matrix);
      } else {
        // reset transform matrix
        this._renderer._resetTransformPath(this);
        this._update();
      }
    }
    return this;
  },

  /**
   * Check if the feature was dragged, that'll supress the click event
   * on mouseup. That fixes popups for example
   *
   * @param  {MouseEvent} e
   */
  _onMouseClick: function _onMouseClick(e) {
    if (this.dragging && this.dragging.moved() || this._map.dragging && this._map.dragging.moved()) {
      return;
    }

    this._fireMouseEvent(e);
  }

});
var END = {
  mousedown: 'mouseup',
  touchstart: 'touchend',
  pointerdown: 'touchend',
  MSPointerDown: 'touchend'
};

var MOVE = {
  mousedown: 'mousemove',
  touchstart: 'touchmove',
  pointerdown: 'touchmove',
  MSPointerDown: 'touchmove'
};

function distance(a, b) {
  var dx = a.x - b.x,
      dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Drag handler
 * @class L.Path.Drag
 * @extends {L.Handler}
 */
L.Handler.PathDrag = L.Handler.extend( /** @lends  L.Path.Drag.prototype */{

  statics: {
    DRAGGING_CLS: 'leaflet-path-draggable'
  },

  /**
   * @param  {L.Path} path
   * @constructor
   */
  initialize: function initialize(path) {

    /**
     * @type {L.Path}
     */
    this._path = path;

    /**
     * @type {Array.<Number>}
     */
    this._matrix = null;

    /**
     * @type {L.Point}
     */
    this._startPoint = null;

    /**
     * @type {L.Point}
     */
    this._dragStartPoint = null;

    /**
     * @type {Boolean}
     */
    this._mapDraggingWasEnabled = false;
  },

  /**
   * Enable dragging
   */
  addHooks: function addHooks() {
    this._path.on('mousedown', this._onDragStart, this);

    this._path.options.className = this._path.options.className ? this._path.options.className + ' ' + L.Handler.PathDrag.DRAGGING_CLS : L.Handler.PathDrag.DRAGGING_CLS;

    if (this._path._path) {
      L.DomUtil.addClass(this._path._path, L.Handler.PathDrag.DRAGGING_CLS);
    }
  },

  /**
   * Disable dragging
   */
  removeHooks: function removeHooks() {
    this._path.off('mousedown', this._onDragStart, this);

    this._path.options.className = this._path.options.className.replace(new RegExp('\\s+' + L.Handler.PathDrag.DRAGGING_CLS), '');
    if (this._path._path) {
      L.DomUtil.removeClass(this._path._path, L.Handler.PathDrag.DRAGGING_CLS);
    }
  },

  /**
   * @return {Boolean}
   */
  moved: function moved() {
    return this._path._dragMoved;
  },

  /**
   * Start drag
   * @param  {L.MouseEvent} evt
   */
  _onDragStart: function _onDragStart(evt) {
    var eventType = evt.originalEvent._simulated ? 'touchstart' : evt.originalEvent.type;

    this._mapDraggingWasEnabled = false;
    this._startPoint = evt.containerPoint.clone();
    this._dragStartPoint = evt.containerPoint.clone();
    this._matrix = [1, 0, 0, 1, 0, 0];
    L.DomEvent.stop(evt.originalEvent);

    L.DomUtil.addClass(this._path._renderer._container, 'leaflet-interactive');
    L.DomEvent.on(document, MOVE[eventType], this._onDrag, this).on(document, END[eventType], this._onDragEnd, this);

    if (this._path._map.dragging.enabled()) {
      // I guess it's required because mousdown gets simulated with a delay
      //this._path._map.dragging._draggable._onUp(evt);

      this._path._map.dragging.disable();
      this._mapDraggingWasEnabled = true;
    }
    this._path._dragMoved = false;

    if (this._path._popup) {
      // that might be a case on touch devices as well
      this._path._popup._close();
    }

    this._replaceCoordGetters(evt);
  },

  /**
   * Dragging
   * @param  {L.MouseEvent} evt
   */
  _onDrag: function _onDrag(evt) {
    L.DomEvent.stop(evt);

    var first = evt.touches && evt.touches.length >= 1 ? evt.touches[0] : evt;
    var containerPoint = this._path._map.mouseEventToContainerPoint(first);

    // skip taps
    if (evt.type === 'touchmove' && !this._path._dragMoved) {
      var totalMouseDragDistance = this._dragStartPoint.distanceTo(containerPoint);
      if (totalMouseDragDistance <= this._path._map.options.tapTolerance) {
        return;
      }
    }

    var x = containerPoint.x;
    var y = containerPoint.y;

    var dx = x - this._startPoint.x;
    var dy = y - this._startPoint.y;

    // Send events only if point was moved
    if (dx || dy) {
      if (!this._path._dragMoved) {
        this._path._dragMoved = true;
        this._path.fire('dragstart', evt);
        // we don't want that to happen on click
        this._path.bringToFront();
      }

      this._matrix[4] += dx;
      this._matrix[5] += dy;

      this._startPoint.x = x;
      this._startPoint.y = y;

      this._path.fire('predrag', evt);
      this._path._transform(this._matrix);
      this._path.fire('drag', evt);
    }
  },

  /**
   * Dragging stopped, apply
   * @param  {L.MouseEvent} evt
   */
  _onDragEnd: function _onDragEnd(evt) {
    var containerPoint = this._path._map.mouseEventToContainerPoint(evt);
    var moved = this.moved();

    // apply matrix
    if (moved) {
      this._transformPoints(this._matrix);
      this._path._updatePath();
      this._path._project();
      this._path._transform(null);

      L.DomEvent.stop(evt);
    }

    L.DomEvent.off(document, 'mousemove touchmove', this._onDrag, this);
    L.DomEvent.off(document, 'mouseup touchend', this._onDragEnd, this);

    this._restoreCoordGetters();

    // consistency
    if (moved) {
      this._path.fire('dragend', {
        distance: distance(this._dragStartPoint, containerPoint)
      });

      // hack for skipping the click in canvas-rendered layers
      var contains = this._path._containsPoint;
      this._path._containsPoint = L.Util.falseFn;
      L.Util.requestAnimFrame(function () {
        L.DomEvent.skipped({ type: 'click' });
        this._path._containsPoint = contains;
      }, this);
    }

    this._matrix = null;
    this._startPoint = null;
    this._dragStartPoint = null;
    this._path._dragMoved = false;

    if (this._mapDraggingWasEnabled) {
      if (moved) L.DomEvent.fakeStop({ type: 'click' });
      this._path._map.dragging.enable();
    }
  },

  /**
   * Applies transformation, does it in one sweep for performance,
   * so don't be surprised about the code repetition.
   *
   * [ x ]   [ a  b  tx ] [ x ]   [ a * x + b * y + tx ]
   * [ y ] = [ c  d  ty ] [ y ] = [ c * x + d * y + ty ]
   *
   * @param {Array.<Number>} matrix
   */
  _transformPoints: function _transformPoints(matrix, dest) {
    var path = this._path;
    var i, len, latlng;

    var px = L.point(matrix[4], matrix[5]);

    var crs = path._map.options.crs;
    var transformation = crs.transformation;
    var scale = crs.scale(path._map.getZoom());
    var projection = crs.projection;

    var diff = transformation.untransform(px, scale).subtract(transformation.untransform(L.point(0, 0), scale));
    var applyTransform = !dest;

    path._bounds = new L.LatLngBounds();

    // console.time('transform');
    // all shifts are in-place
    if (path._point) {
      // L.Circle
      dest = projection.unproject(projection.project(path._latlng)._add(diff));
      if (applyTransform) {
        path._latlng = dest;
        path._point._add(px);
      }
    } else if (path._rings || path._parts) {
      // everything else
      var rings = path._rings || path._parts;
      var latlngs = path._latlngs;
      dest = dest || latlngs;
      if (!L.Util.isArray(latlngs[0])) {
        // polyline
        latlngs = [latlngs];
        dest = [dest];
      }
      for (i = 0, len = rings.length; i < len; i++) {
        dest[i] = dest[i] || [];
        for (var j = 0, jj = rings[i].length; j < jj; j++) {
          latlng = latlngs[i][j];
          dest[i][j] = projection.unproject(projection.project(latlng)._add(diff));
          if (applyTransform) {
            path._bounds.extend(latlngs[i][j]);
            rings[i][j]._add(px);
          }
        }
      }
    }
    return dest;
    // console.timeEnd('transform');
  },

  /**
   * If you want to read the latlngs during the drag - your right,
   * but they have to be transformed
   */
  _replaceCoordGetters: function _replaceCoordGetters() {
    if (this._path.getLatLng) {
      // Circle, CircleMarker
      this._path.getLatLng_ = this._path.getLatLng;
      this._path.getLatLng = L.Util.bind(function () {
        return this.dragging._transformPoints(this.dragging._matrix, {});
      }, this._path);
    } else if (this._path.getLatLngs) {
      this._path.getLatLngs_ = this._path.getLatLngs;
      this._path.getLatLngs = L.Util.bind(function () {
        return this.dragging._transformPoints(this.dragging._matrix, []);
      }, this._path);
    }
  },

  /**
   * Put back the getters
   */
  _restoreCoordGetters: function _restoreCoordGetters() {
    if (this._path.getLatLng_) {
      this._path.getLatLng = this._path.getLatLng_;
      delete this._path.getLatLng_;
    } else if (this._path.getLatLngs_) {
      this._path.getLatLngs = this._path.getLatLngs_;
      delete this._path.getLatLngs_;
    }
  }

});

/**
 * @param  {L.Path} layer
 * @return {L.Path}
 */
L.Handler.PathDrag.makeDraggable = function (layer) {
  layer.dragging = new L.Handler.PathDrag(layer);
  return layer;
};

/**
 * Also expose as a method
 * @return {L.Path}
 */
L.Path.prototype.makeDraggable = function () {
  return L.Handler.PathDrag.makeDraggable(this);
};

L.Path.addInitHook(function () {
  if (this.options.draggable) {
    // ensure interactive
    this.options.interactive = true;

    if (this.dragging) {
      this.dragging.enable();
    } else {
      L.Handler.PathDrag.makeDraggable(this);
      this.dragging.enable();
    }
  } else if (this.dragging) {
    this.dragging.disable();
  }
});
L.SVG.include({

  /**
   * Reset transform matrix
   */
  _resetTransformPath: function _resetTransformPath(layer) {
    layer._path.setAttributeNS(null, 'transform', '');
  },

  /**
   * Applies matrix transformation to SVG
   * @param {L.Path}         layer
   * @param {Array.<Number>} matrix
   */
  transformPath: function transformPath(layer, matrix) {
    layer._path.setAttributeNS(null, 'transform', 'matrix(' + matrix.join(' ') + ')');
  }

});
L.SVG.include(!L.Browser.vml ? {} : {

  /**
   * Reset transform matrix
   */
  _resetTransformPath: function _resetTransformPath(layer) {
    if (layer._skew) {
      // super important! workaround for a 'jumping' glitch:
      // disable transform before removing it
      layer._skew.on = false;
      layer._path.removeChild(layer._skew);
      layer._skew = null;
    }
  },

  /**
   * Applies matrix transformation to VML
   * @param {L.Path}         layer
   * @param {Array.<Number>} matrix
   */
  transformPath: function transformPath(layer, matrix) {
    var skew = layer._skew;

    if (!skew) {
      skew = L.SVG.create('skew');
      layer._path.appendChild(skew);
      skew.style.behavior = 'url(#default#VML)';
      layer._skew = skew;
    }

    // handle skew/translate separately, cause it's broken
    var mt = matrix[0].toFixed(8) + ' ' + matrix[1].toFixed(8) + ' ' + matrix[2].toFixed(8) + ' ' + matrix[3].toFixed(8) + ' 0 0';
    var offset = Math.floor(matrix[4]).toFixed() + ', ' + Math.floor(matrix[5]).toFixed() + '';

    var s = this._path.style;
    var l = parseFloat(s.left);
    var t = parseFloat(s.top);
    var w = parseFloat(s.width);
    var h = parseFloat(s.height);

    if (isNaN(l)) l = 0;
    if (isNaN(t)) t = 0;
    if (isNaN(w) || !w) w = 1;
    if (isNaN(h) || !h) h = 1;

    var origin = (-l / w - 0.5).toFixed(8) + ' ' + (-t / h - 0.5).toFixed(8);

    skew.on = 'f';
    skew.matrix = mt;
    skew.origin = origin;
    skew.offset = offset;
    skew.on = true;
  }

});
function TRUE_FN() {
  return true;
}

L.Canvas.include({

  /**
   * Do nothing
   * @param  {L.Path} layer
   */
  _resetTransformPath: function _resetTransformPath(layer) {
    if (!this._containerCopy) return;

    delete this._containerCopy;

    if (layer._containsPoint_) {
      layer._containsPoint = layer._containsPoint_;
      delete layer._containsPoint_;

      this._requestRedraw(layer);
    }
  },

  /**
   * Algorithm outline:
   *
   * 1. pre-transform - clear the path out of the canvas, copy canvas state
   * 2. at every frame:
   *    2.1. save
   *    2.2. redraw the canvas from saved one
   *    2.3. transform
   *    2.4. draw path
   *    2.5. restore
   * 3. Repeat
   *
   * @param  {L.Path}         layer
   * @param  {Array.<Number>} matrix
   */
  transformPath: function transformPath(layer, matrix) {
    var copy = this._containerCopy;
    var ctx = this._ctx,
        copyCtx;
    var m = L.Browser.retina ? 2 : 1;
    var bounds = this._bounds;
    var size = bounds.getSize();
    var pos = bounds.min;

    if (!copy) {
      // get copy of all rendered layers
      copy = this._containerCopy = document.createElement('canvas');
      copyCtx = copy.getContext('2d');
      // document.body.appendChild(copy);

      copy.width = m * size.x;
      copy.height = m * size.y;

      this._removePath(layer);
      this._redraw();

      copyCtx.translate(m * bounds.min.x, m * bounds.min.y);
      copyCtx.drawImage(this._container, 0, 0);
      this._initPath(layer);

      // avoid flickering because of the 'mouseover's
      layer._containsPoint_ = layer._containsPoint;
      layer._containsPoint = TRUE_FN;
    }

    ctx.save();
    ctx.clearRect(pos.x, pos.y, size.x * m, size.y * m);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.restore();
    ctx.save();

    ctx.drawImage(this._containerCopy, 0, 0, size.x, size.y);
    ctx.transform.apply(ctx, matrix);

    // now draw one layer only
    this._drawing = true;
    layer._updatePath();
    this._drawing = false;

    ctx.restore();
  }

});
/**
 * Drag/rotate/resize handler for [leaflet](http://leafletjs.com) vector features.
 *
 * @author Alexander Milevski <info@w8r.name>
 * @license MIT
 * @preserve
 */
/**
 * @namespace
 * @type {Object}
 */
L.PathTransform = {};

/**
 * Point on the line segment or its extention
 *
 * @param  {L.Point} start
 * @param  {L.Point} final
 * @param  {Number}  distPx
 * @return {L.Point}
 */
L.PathTransform.pointOnLine = function (start, final, distPx) {
  var ratio = 1 + distPx / start.distanceTo(final);
  return new L.Point(start.x + (final.x - start.x) * ratio, start.y + (final.y - start.y) * ratio);
};

/**
 * Deep merge objects.
 */
L.PathTransform.merge = function () {
  var i = 1;
  var key, val;
  var obj = arguments[i];

  function isObject(object) {
    return Object.prototype.toString.call(object) === '[object Object]';
  }

  // make sure we don't modify source element and it's properties
  // objects are passed by reference
  var target = arguments[0];

  while (obj) {
    obj = arguments[i++];
    for (key in obj) {
      if (!obj.hasOwnProperty(key)) {
        continue;
      }

      val = obj[key];

      if (isObject(val) && isObject(target[key])) {
        target[key] = L.Util.merge(target[key], val);
      } else {
        target[key] = val;
      }
    }
  }
  return target;
};
/**
 * @class  L.Matrix
 *
 * @param {Number} a
 * @param {Number} b
 * @param {Number} c
 * @param {Number} d
 * @param {Number} e
 * @param {Number} f
 */
L.Matrix = function (a, b, c, d, e, f) {

  /**
   * @type {Array.<Number>}
   */
  this._matrix = [a, b, c, d, e, f];
};

L.Matrix.prototype = {

  /**
   * @param  {L.Point} point
   * @return {L.Point}
   */
  transform: function transform(point) {
    return this._transform(point.clone());
  },

  /**
   * Destructive
   *
   * [ x ] = [ a  b  tx ] [ x ] = [ a * x + b * y + tx ]
   * [ y ] = [ c  d  ty ] [ y ] = [ c * x + d * y + ty ]
   *
   * @param  {L.Point} point
   * @return {L.Point}
   */
  _transform: function _transform(point) {
    var matrix = this._matrix;
    var x = point.x,
        y = point.y;
    point.x = matrix[0] * x + matrix[1] * y + matrix[4];
    point.y = matrix[2] * x + matrix[3] * y + matrix[5];
    return point;
  },

  /**
   * @param  {L.Point} point
   * @return {L.Point}
   */
  untransform: function untransform(point) {
    var matrix = this._matrix;
    return new L.Point((point.x / matrix[0] - matrix[4]) / matrix[0], (point.y / matrix[2] - matrix[5]) / matrix[2]);
  },

  /**
   * @return {L.Matrix}
   */
  clone: function clone() {
    var matrix = this._matrix;
    return new L.Matrix(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
  },

  /**
   * @param {L.Point=|Number=} translate
   * @return {L.Matrix|L.Point}
   */
  translate: function translate(_translate) {
    if (_translate === undefined) {
      return new L.Point(this._matrix[4], this._matrix[5]);
    }

    var translateX, translateY;
    if (typeof _translate === 'number') {
      translateX = translateY = _translate;
    } else {
      translateX = _translate.x;
      translateY = _translate.y;
    }

    return this._add(1, 0, 0, 1, translateX, translateY);
  },

  /**
   * @param {L.Point=|Number=} scale
   * @return {L.Matrix|L.Point}
   */
  scale: function scale(_scale, origin) {
    if (_scale === undefined) {
      return new L.Point(this._matrix[0], this._matrix[3]);
    }

    var scaleX, scaleY;
    origin = origin || L.point(0, 0);
    if (typeof _scale === 'number') {
      scaleX = scaleY = _scale;
    } else {
      scaleX = _scale.x;
      scaleY = _scale.y;
    }

    return this._add(scaleX, 0, 0, scaleY, origin.x, origin.y)._add(1, 0, 0, 1, -origin.x, -origin.y);
  },

  /**
   * m00  m01  x - m00 * x - m01 * y
   * m10  m11  y - m10 * x - m11 * y
   * @param {Number}   angle
   * @param {L.Point=} origin
   * @return {L.Matrix}
   */
  rotate: function rotate(angle, origin) {
    var cos = Math.cos(angle);
    var sin = Math.sin(angle);

    origin = origin || new L.Point(0, 0);

    return this._add(cos, sin, -sin, cos, origin.x, origin.y)._add(1, 0, 0, 1, -origin.x, -origin.y);
  },

  /**
   * Invert rotation
   * @return {L.Matrix}
   */
  flip: function flip() {
    this._matrix[1] *= -1;
    this._matrix[2] *= -1;
    return this;
  },

  /**
   * @param {Number|L.Matrix} a
   * @param {Number} b
   * @param {Number} c
   * @param {Number} d
   * @param {Number} e
   * @param {Number} f
   */
  _add: function _add(a, b, c, d, e, f) {
    var result = [[], [], []];
    var src = this._matrix;
    var m = [[src[0], src[2], src[4]], [src[1], src[3], src[5]], [0, 0, 1]];
    var other = [[a, c, e], [b, d, f], [0, 0, 1]],
        val;

    if (a && a instanceof L.Matrix) {
      src = a._matrix;
      other = [[src[0], src[2], src[4]], [src[1], src[3], src[5]], [0, 0, 1]];
    }

    for (var i = 0; i < 3; i++) {
      for (var j = 0; j < 3; j++) {
        val = 0;
        for (var k = 0; k < 3; k++) {
          val += m[i][k] * other[k][j];
        }
        result[i][j] = val;
      }
    }

    this._matrix = [result[0][0], result[1][0], result[0][1], result[1][1], result[0][2], result[1][2]];
    return this;
  }

};

L.matrix = function (a, b, c, d, e, f) {
  return new L.Matrix(a, b, c, d, e, f);
};

/**
 * Marker handler
 * @extends {L.CircleMarker}
 */
L.PathTransform.Handle = L.CircleMarker.extend({
  options: {
    className: 'leaflet-path-transform-handler'
  },

  onAdd: function onAdd(map) {
    L.CircleMarker.prototype.onAdd.call(this, map);
    if (this._path && this.options.setCursor) {
      // SVG/VML
      this._path.style.cursor = L.PathTransform.Handle.CursorsByType[this.options.index];
    }
  }
});

/**
 * @const
 * @type {Array}
 */
L.PathTransform.Handle.CursorsByType = ['nesw-resize', 'nwse-resize', 'nesw-resize', 'nwse-resize'];

/**
 * @extends {L.Handler.PathTransform.Handle}
 */
L.PathTransform.RotateHandle = L.PathTransform.Handle.extend({
  options: {
    className: 'leaflet-path-transform-handler transform-handler--rotate'
  },

  onAdd: function onAdd(map) {
    L.CircleMarker.prototype.onAdd.call(this, map);
    if (this._path && this.options.setCursor) {
      // SVG/VML
      this._path.style.cursor = 'all-scroll';
    }
  }
});

L.Handler.PathTransform = L.Handler.extend({

  options: {
    rotation: true,
    scaling: true,
    uniformScaling: true,
    maxZoom: 22,

    // edge handlers
    handlerOptions: {
      radius: 5,
      fillColor: '#ffffff',
      color: '#202020',
      fillOpacity: 1,
      weight: 2,
      opacity: 0.7,
      setCursor: true
    },

    // rectangle
    boundsOptions: {
      weight: 1,
      opacity: 1,
      dashArray: [3, 3],
      fill: false
    },

    // rotation handler
    rotateHandleOptions: {
      weight: 1,
      opacity: 1,
      setCursor: true
    },
    // rotation handle length
    handleLength: 20,

    // maybe I'll add skewing in the future
    edgesCount: 4,

    handleClass: L.PathTransform.Handle,
    rotateHandleClass: L.PathTransform.RotateHandle
  },

  /**
   * @class L.Handler.PathTransform
   * @constructor
   * @param  {L.Path} path
   */
  initialize: function initialize(path) {
    // references
    this._path = path;
    this._map = null;

    // handlers
    this._activeMarker = null;
    this._originMarker = null;
    this._rotationMarker = null;

    // origins & temporary state
    this._rotationOrigin = null;
    this._scaleOrigin = null;
    this._angle = 0;
    this._scale = L.point(1, 1);
    this._initialDist = 0;
    this._initialDistX = 0;
    this._initialDistY = 0;
    this._rotationStart = null;
    this._rotationOriginPt = null;

    // preview and transform matrix
    this._matrix = new L.Matrix(1, 0, 0, 1, 0, 0);
    this._projectedMatrix = new L.Matrix(1, 0, 0, 1, 0, 0);

    // ui elements
    this._handlersGroup = null;
    this._rect = null;
    this._handlers = [];
    this._handleLine = null;
  },

  /**
   * If the polygon is not rendered, you can transform it yourself
   * in the coordinates, and do it properly.
   * @param {Object=} options
   */
  enable: function enable(options) {
    if (this._path._map) {
      this._map = this._path._map;
      if (options) {
        this.setOptions(options);
      }
      L.Handler.prototype.enable.call(this);
    }
  },

  /**
   * Init interactions and handlers
   */
  addHooks: function addHooks() {
    this._createHandlers();
    this._path.on('dragstart', this._onDragStart, this).on('dragend', this._onDragEnd, this);
  },

  /**
   * Remove handlers
   */
  removeHooks: function removeHooks() {
    this._hideHandlers();
    this._path.off('dragstart', this._onDragStart, this).off('dragend', this._onDragEnd, this);
    this._handlersGroup = null;
    this._rect = null;
    this._handlers = [];
  },

  /**
   * Change editing options
   * @param {Object} options
   */
  setOptions: function setOptions(options) {
    var enabled = this._enabled;
    if (enabled) {
      this.disable();
    }

    this.options = L.PathTransform.merge({}, L.Handler.PathTransform.prototype.options, options);

    if (enabled) {
      this.enable();
    }

    return this;
  },

  /**
   * @param  {Number}   angle
   * @param  {L.LatLng} origin
   * @return {L.Handler.PathTransform}
   */
  rotate: function rotate(angle, origin) {
    return this.transform(angle, null, origin);
  },

  /**
   * @param  {L.Point|Number} scale
   * @param  {L.LatLng}       origin
   * @return {L.Handler.PathTransform}
   */
  scale: function scale(_scale2, origin) {
    if (typeof _scale2 === 'number') {
      _scale2 = L.point(_scale2, _scale2);
    }
    return this.transform(0, _scale2, null, origin);
  },

  /**
   * @param  {Number}    angle
   * @param  {L.Point}   scale
   * @param  {L.LatLng=} rotationOrigin
   * @param  {L.LatLng=} scaleOrigin
   * @return {L.Handler.PathTransform}
   */
  transform: function transform(angle, scale, rotationOrigin, scaleOrigin) {
    var center = this._path.getCenter();
    rotationOrigin = rotationOrigin || center;
    scaleOrigin = scaleOrigin || center;
    this._map = this._path._map;
    this._transformPoints(this._path, angle, scale, rotationOrigin, scaleOrigin);
    return this;
  },

  /**
   * Update the polygon and handlers preview, no reprojection
   */
  _update: function _update() {
    var matrix = this._matrix;

    // update handlers
    for (var i = 0, len = this._handlers.length; i < len; i++) {
      var handler = this._handlers[i];
      if (handler !== this._originMarker) {
        handler._point = matrix.transform(handler._initialPoint);
        handler._updatePath();
      }
    }

    matrix = matrix.clone().flip();

    this._applyTransform(matrix);
    this._path.fire('transform', { layer: this._path });
  },

  /**
   * @param  {L.Matrix} matrix
   */
  _applyTransform: function _applyTransform(matrix) {
    this._path._transform(matrix._matrix);
    this._rect._transform(matrix._matrix);

    if (this.options.rotation) {
      this._handleLine._transform(matrix._matrix);
    }
  },

  /**
   * Apply final transformation
   */
  _apply: function _apply() {
    //console.group('apply transform');
    var map = this._map;
    var matrix = this._matrix.clone();
    var angle = this._angle;
    var scale = this._scale.clone();

    this._transformGeometries();

    // update handlers
    for (var i = 0, len = this._handlers.length; i < len; i++) {
      var handler = this._handlers[i];
      handler._latlng = map.layerPointToLatLng(handler._point);
      delete handler._initialPoint;
      handler.redraw();
    }

    this._matrix = L.matrix(1, 0, 0, 1, 0, 0);
    this._scale = L.point(1, 1);
    this._angle = 0;

    this._updateHandlers();

    map.dragging.enable();
    this._path.fire('transformed', {
      matrix: matrix,
      scale: scale,
      rotation: angle,
      // angle: angle * (180 / Math.PI),
      layer: this._path
    });
    // console.groupEnd('apply transform');
  },

  /**
   * Use this method to completely reset handlers, if you have changed the
   * geometry of transformed layer
   */
  reset: function reset() {
    if (this._enabled) {
      if (this._rect) {
        this._handlersGroup.removeLayer(this._rect);
        this._rect = this._getBoundingPolygon().addTo(this._handlersGroup);
      }
      this._updateHandlers();
    }
  },

  /**
   * Recalculate rotation handlers position
   */
  _updateHandlers: function _updateHandlers() {
    var handlersGroup = this._handlersGroup;

    this._rectShape = this._rect.toGeoJSON();

    if (this._handleLine) {
      this._handlersGroup.removeLayer(this._handleLine);
    }

    if (this._rotationMarker) {
      this._handlersGroup.removeLayer(this._rotationMarker);
    }

    this._handleLine = this._rotationMarker = null;

    for (var i = this._handlers.length - 1; i >= 0; i--) {
      handlersGroup.removeLayer(this._handlers[i]);
    }

    this._createHandlers();
  },

  /**
   * Transform geometries separately
   */
  _transformGeometries: function _transformGeometries() {
    this._path._transform(null);
    this._rect._transform(null);

    this._transformPoints(this._path);
    this._transformPoints(this._rect);

    if (this.options.rotation) {
      this._handleLine._transform(null);
      this._transformPoints(this._handleLine, this._angle, null, this._origin);
    }
  },

  /**
   * @param {Number} angle
   * @param {Number} scale
   * @param {L.LatLng=} rotationOrigin
   * @param {L.LatLng=} scaleOrigin
   */
  _getProjectedMatrix: function _getProjectedMatrix(angle, scale, rotationOrigin, scaleOrigin) {
    var map = this._map;
    var zoom = map.getMaxZoom() || this.options.maxZoom;
    var matrix = L.matrix(1, 0, 0, 1, 0, 0);
    var origin;

    angle = angle || this._angle || 0;
    scale = scale || this._scale || L.point(1, 1);

    if (!(scale.x === 1 && scale.y === 1)) {
      scaleOrigin = scaleOrigin || this._scaleOrigin;
      origin = map.project(scaleOrigin, zoom);
      matrix = matrix._add(L.matrix(1, 0, 0, 1, origin.x, origin.y))._add(L.matrix(scale.x, 0, 0, scale.y, 0, 0))._add(L.matrix(1, 0, 0, 1, -origin.x, -origin.y));
    }

    if (angle) {
      rotationOrigin = rotationOrigin || this._rotationOrigin;
      origin = map.project(rotationOrigin, zoom);
      matrix = matrix.rotate(angle, origin).flip();
    }

    return matrix;
  },

  /**
   * @param  {L.LatLng} latlng
   * @param  {L.Matrix} matrix
   * @param  {L.Map}    map
   * @param  {Number}   zoom
   * @return {L.LatLng}
   */
  _transformPoint: function _transformPoint(latlng, matrix, map, zoom) {
    return map.unproject(matrix.transform(map.project(latlng, zoom)), zoom);
  },

  /**
   * Applies transformation, does it in one sweep for performance,
   * so don't be surprised about the code repetition.
   *
   * @param {L.Path}    path
   * @param {Number=}   angle
   * @param {L.Point=}  scale
   * @param {L.LatLng=} rotationOrigin
   * @param {L.LatLng=} scaleOrigin
   */
  _transformPoints: function _transformPoints(path, angle, scale, rotationOrigin, scaleOrigin) {
    var map = path._map;
    var zoom = map.getMaxZoom() || this.options.maxZoom;
    var i, len;

    var projectedMatrix = this._projectedMatrix = this._getProjectedMatrix(angle, scale, rotationOrigin, scaleOrigin);
    // console.time('transform');

    // all shifts are in-place
    if (path._point) {
      // L.Circle
      path._latlng = this._transformPoint(path._latlng, projectedMatrix, map, zoom);
    } else if (path._rings || path._parts) {
      // everything else
      var rings = path._rings;
      var latlngs = path._latlngs;
      path._bounds = new L.LatLngBounds();

      if (!L.Util.isArray(latlngs[0])) {
        // polyline
        latlngs = [latlngs];
      }
      for (i = 0, len = rings.length; i < len; i++) {
        for (var j = 0, jj = rings[i].length; j < jj; j++) {
          latlngs[i][j] = this._transformPoint(latlngs[i][j], projectedMatrix, map, zoom);
          path._bounds.extend(latlngs[i][j]);
        }
      }
    }

    path._reset();
    //console.timeEnd('transform');
  },

  /**
   * Creates markers and handles
   */
  _createHandlers: function _createHandlers() {
    var map = this._map;
    this._handlersGroup = this._handlersGroup || new L.LayerGroup().addTo(map);
    this._rect = this._rect || this._getBoundingPolygon().addTo(this._handlersGroup);

    if (this.options.scaling) {
      this._handlers = [];
      for (var i = 0; i < this.options.edgesCount; i++) {
        // TODO: add stretching
        this._handlers.push(this._createHandler(this._rect._latlngs[0][i], i * 2, i).addTo(this._handlersGroup));
      }
    }

    // add bounds
    if (this.options.rotation) {
      //add rotation handler
      this._createRotationHandlers();
    }
  },

  /**
   * Rotation marker and small connectin handle
   */
  _createRotationHandlers: function _createRotationHandlers() {
    var map = this._map;
    var latlngs = this._rect._latlngs[0];

    var bottom = new L.LatLng((latlngs[0].lat + latlngs[3].lat) / 2, (latlngs[0].lng + latlngs[3].lng) / 2);
    // hehe, top is a reserved word
    var topPoint = new L.LatLng((latlngs[1].lat + latlngs[2].lat) / 2, (latlngs[1].lng + latlngs[2].lng) / 2);

    var handlerPosition = map.layerPointToLatLng(L.PathTransform.pointOnLine(map.latLngToLayerPoint(bottom), map.latLngToLayerPoint(topPoint), this.options.handleLength));

    this._handleLine = new L.Polyline([topPoint, handlerPosition], this.options.rotateHandleOptions).addTo(this._handlersGroup);
    var RotateHandleClass = this.options.rotateHandleClass;
    this._rotationMarker = new RotateHandleClass(handlerPosition, this.options.handlerOptions).addTo(this._handlersGroup).on('mousedown', this._onRotateStart, this);

    this._rotationOrigin = new L.LatLng((topPoint.lat + bottom.lat) / 2, (topPoint.lng + bottom.lng) / 2);

    this._handlers.push(this._rotationMarker);
  },

  /**
   * @return {L.LatLng}
   */
  _getRotationOrigin: function _getRotationOrigin() {
    var latlngs = this._rect._latlngs[0];
    var lb = latlngs[0];
    var rt = latlngs[2];

    return new L.LatLng((lb.lat + rt.lat) / 2, (lb.lng + rt.lng) / 2);
  },

  /**
   * Secure the rotation origin
   * @param  {Event} evt
   */
  _onRotateStart: function _onRotateStart(evt) {
    var map = this._map;

    map.dragging.disable();

    this._originMarker = null;
    this._rotationOriginPt = map.latLngToLayerPoint(this._getRotationOrigin());
    this._rotationStart = evt.layerPoint;
    this._initialMatrix = this._matrix.clone();

    this._angle = 0;
    this._path._map.on('mousemove', this._onRotate, this).on('mouseup', this._onRotateEnd, this);

    this._cachePoints();
    this._path.fire('transformstart', { layer: this._path }).fire('rotatestart', { layer: this._path, rotation: 0 });
  },

  /**
   * @param  {Event} evt
   */
  _onRotate: function _onRotate(evt) {
    var pos = evt.layerPoint;
    var previous = this._rotationStart;
    var origin = this._rotationOriginPt;

    // rotation step angle
    this._angle = Math.atan2(pos.y - origin.y, pos.x - origin.x) - Math.atan2(previous.y - origin.y, previous.x - origin.x);

    this._matrix = this._initialMatrix.clone().rotate(this._angle, origin).flip();

    this._update();
    this._path.fire('rotate', { layer: this._path, rotation: this._angle });
  },

  /**
   * @param  {Event} evt
   */
  _onRotateEnd: function _onRotateEnd(evt) {
    this._path._map.off('mousemove', this._onRotate, this).off('mouseup', this._onRotateEnd, this);

    this._apply();
    this._path.fire('rotateend', { layer: this._path, rotation: this._angle });
  },

  /**
   * @param  {Event} evt
   */
  _onScaleStart: function _onScaleStart(evt) {
    var marker = evt.target;
    var map = this._map;

    map.dragging.disable();

    this._activeMarker = marker;

    this._originMarker = this._handlers[(marker.options.index + 2) % 4];
    this._scaleOrigin = this._originMarker.getLatLng();

    this._initialMatrix = this._matrix.clone();
    this._cachePoints();

    this._map.on('mousemove', this._onScale, this).on('mouseup', this._onScaleEnd, this);
    this._initialDist = this._originMarker._point.distanceTo(this._activeMarker._point);
    this._initialDistX = this._originMarker._point.x - this._activeMarker._point.x;
    this._initialDistY = this._originMarker._point.y - this._activeMarker._point.y;

    this._path.fire('transformstart', { layer: this._path }).fire('scalestart', { layer: this._path, scale: L.point(1, 1) });

    this._map.removeLayer(this._handleLine);
    this._map.removeLayer(this._rotationMarker);

    //this._handleLine = this._rotationMarker = null;
  },

  /**
   * @param  {Event} evt
   */
  _onScale: function _onScale(evt) {
    var originPoint = this._originMarker._point;
    var ratioX, ratioY;
    if (this.options.uniformScaling) {
      ratioX = originPoint.distanceTo(evt.layerPoint) / this._initialDist;
      ratioY = ratioX;
    } else {
      ratioX = (originPoint.x - evt.layerPoint.x) / this._initialDistX;
      ratioY = (originPoint.y - evt.layerPoint.y) / this._initialDistY;
    }

    this._scale = new L.Point(ratioX, ratioY);

    // update matrix
    this._matrix = this._initialMatrix.clone().scale(this._scale, originPoint);

    this._update();
    this._path.fire('scale', {
      layer: this._path, scale: this._scale.clone() });
  },

  /**
   * Scaling complete
   * @param  {Event} evt
   */
  _onScaleEnd: function _onScaleEnd(evt) {
    this._map.off('mousemove', this._onScale, this).off('mouseup', this._onScaleEnd, this);

    this._map.addLayer(this._handleLine);
    this._map.addLayer(this._rotationMarker);

    this._apply();
    this._path.fire('scaleend', {
      layer: this._path, scale: this._scale.clone() });
  },

  /**
   * Cache current handlers positions
   */
  _cachePoints: function _cachePoints() {
    this._handlersGroup.eachLayer(function (layer) {
      layer.bringToFront();
    });
    for (var i = 0, len = this._handlers.length; i < len; i++) {
      var handler = this._handlers[i];
      handler._initialPoint = handler._point.clone();
    }
  },

  /**
   * Bounding polygon
   * @return {L.Polygon}
   */
  _getBoundingPolygon: function _getBoundingPolygon() {
    if (this._rectShape) {
      return L.GeoJSON.geometryToLayer(this._rectShape, this.options.boundsOptions);
    } else {
      return new L.Rectangle(this._path.getBounds(), this.options.boundsOptions);
    }
  },

  /**
   * Create corner marker
   * @param  {L.LatLng} latlng
   * @param  {Number}   type one of L.Handler.PathTransform.HandlerTypes
   * @param  {Number}   index
   * @return {L.Handler.PathTransform.Handle}
   */
  _createHandler: function _createHandler(latlng, type, index) {
    var HandleClass = this.options.handleClass;
    var marker = new HandleClass(latlng, L.Util.extend({}, this.options.handlerOptions, {
      className: 'leaflet-drag-transform-marker drag-marker--' + index + ' drag-marker--' + type,
      index: index,
      type: type
    }));

    marker.on('mousedown', this._onScaleStart, this);
    return marker;
  },

  /**
   * Hide(not remove) the handlers layer
   */
  _hideHandlers: function _hideHandlers() {
    this._map.removeLayer(this._handlersGroup);
  },

  /**
   * Hide handlers and rectangle
   */
  _onDragStart: function _onDragStart() {
    this._hideHandlers();
  },

  /**
   * Drag rectangle, re-create handlers
   */
  _onDragEnd: function _onDragEnd(evt) {
    var rect = this._rect;
    var matrix = (evt.layer ? evt.layer : this._path).dragging._matrix.slice();

    if (!rect.dragging) {
      rect.dragging = new L.Handler.PathDrag(rect);
    }
    rect.dragging.enable();
    this._map.addLayer(rect);
    rect.dragging._transformPoints(matrix);
    rect._updatePath();
    rect._project();
    rect.dragging.disable();

    this._map.addLayer(this._handlersGroup);
    this._updateHandlers();

    this._path.fire('transformed', {
      scale: L.point(1, 1),
      rotation: 0,
      matrix: L.matrix.apply(undefined, matrix),
      translate: L.point(matrix[4], matrix[5]),
      layer: this._path
    });
  }
});

L.Path.addInitHook(function () {
  if (this.options.transform) {
    this.transform = new L.Handler.PathTransform(this, this.options.transform);
  }
});

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var ReactDistortableImageOverlayMapLayer = function (_MapLayer) {
	inherits(ReactDistortableImageOverlayMapLayer, _MapLayer);

	function ReactDistortableImageOverlayMapLayer() {
		classCallCheck(this, ReactDistortableImageOverlayMapLayer);
		return possibleConstructorReturn(this, (ReactDistortableImageOverlayMapLayer.__proto__ || Object.getPrototypeOf(ReactDistortableImageOverlayMapLayer)).apply(this, arguments));
	}

	createClass(ReactDistortableImageOverlayMapLayer, [{
		key: 'createLeafletElement',
		value: function createLeafletElement(props) {
			var _this2 = this;

			this.distortableImage = new L.DistortableImageOverlay(props.url, this.getOptions(props));

			L.DomEvent.on(this.distortableImage, 'load', function () {
				_this2.distortableImage._image.style.opacity = _this2.props.opacity;
				_this2.handleEditModeState(props.editMode);
			}, this.distortableImage);

			this.distortableImage.on('edit', function (update) {
				_this2.props.onUpdate(update.sourceTarget._corners);
			}, this.distortableImage);

			return this.distortableImage;
		}
	}, {
		key: 'updateLeafletElement',
		value: function updateLeafletElement(fromProps, toProps) {
			var _this3 = this;

			// Keep map ref before removing so we can addLayer when the LeafletElement is recreated
			var map = this.distortableImage._map;
			this.distortableImage.onRemove();

			// The translation state behaves differently from the rotate and distort (uses leaflet-path-transform)
			// We hold the translated corners in a local var and wipe it after each translation
			if (this.translateUpdateCorners !== undefined) {
				this.distortableImage = new L.DistortableImageOverlay(toProps.url, { corners: this.translateUpdateCorners });
				this.translateUpdateCorners = undefined;
			} else {
				this.distortableImage = new L.DistortableImageOverlay(toProps.url, this.getOptions(toProps));
			}

			// Apply opacity after the image loads
			L.DomEvent.on(this.distortableImage, 'load', function () {
				_this3.distortableImage._image.style.opacity = _this3.props.opacity;
			}, this.distortableImage);

			// Re-add the update listener
			this.distortableImage.on('edit', function (update) {
				_this3.props.onUpdate(update.sourceTarget._corners);
			}, this.distortableImage);

			map.addLayer(this.distortableImage);

			this.endTranslate();
			this.handleEditModeState(toProps.editMode);
		}
	}, {
		key: 'handleEditModeState',
		value: function handleEditModeState(editMode) {
			switch (editMode) {
				case 'rotate':
					this.distortableImage.editing.enable();
					this.distortableImage.editing._toggleRotateDistort();
					this.distortableImage._image.style.opacity = this.props.opacity;
					break;

				case 'distort':
					this.distortableImage.editing.enable();
					this.distortableImage._image.style.opacity = this.props.opacity;
					break;

				case 'translate':
					this.startTranslate();
					break;

				case 'scale':
					this.distortableImage.editing.enable();
					this.distortableImage.editing._toggleScale();
					this.distortableImage._image.style.opacity = this.props.opacity;
					break;

				default:
					this.distortableImage.editing.disable();
					this.distortableImage._image.style.opacity = this.props.opacity;
			}
		}
	}, {
		key: 'startTranslate',
		value: function startTranslate() {
			var _this4 = this;

			var map = this.distortableImage._map;
			var corners = this.distortableImage.getCorners();

			// DistortableImageOverlay corners have different ordering from L.Polygon bounds
			var polygonDragCorners = [corners[0], corners[1], corners[3], corners[2]];

			this.translatePolygon = new L.Polygon(polygonDragCorners, { draggable: true, transform: true, color: 'red', fill: true });
			this.distortableImage._image.style.opacity = this.props.opacity;
			this.translatePolygon.transform.enable();

			this.translatePolygon.on('dragend', function (event) {

				var latlngs = event.target.getLatLngs()[0];
				var newImageCorners = [latlngs[0], latlngs[1], latlngs[3], latlngs[2]];

				// Without this timeout it crashes trying to access the map ref of a removed element.
				// Not sure why..
				setTimeout(function () {
					_this4.props.onUpdate(newImageCorners);
				}, 10);
				map.removeLayer(_this4.distortableImage);

				_this4.distortableImage = new L.DistortableImageOverlay(_this4.props.url, { corners: newImageCorners });
				_this4.distortableImage.addTo(map);
				_this4.distortableImage._image.style.opacity = _this4.props.opacity;

				_this4.translateUpdateCorners = newImageCorners;
			});

			this.translatePolygon.addTo(map);
		}
	}, {
		key: 'endTranslate',
		value: function endTranslate() {
			var map = this.distortableImage._map;
			if (this.translatePolygon) {
				map.removeLayer(this.translatePolygon);
			}
		}
	}]);
	return ReactDistortableImageOverlayMapLayer;
}(MapLayer);

var ReactDistortableImageOverlayMapLayer$1 = withLeaflet(ReactDistortableImageOverlayMapLayer);

// This class acts as a geometry state container for the Leaflet MapLayer component.
// The corners of the ImageOverlay are passed in as props, held as state and changes
// are passed upstream with the `onCornersUpdated` method
var ReactDistortableImageOverlay = function (_React$Component) {
  inherits(ReactDistortableImageOverlay, _React$Component);

  function ReactDistortableImageOverlay(props) {
    classCallCheck(this, ReactDistortableImageOverlay);

    var _this = possibleConstructorReturn(this, (ReactDistortableImageOverlay.__proto__ || Object.getPrototypeOf(ReactDistortableImageOverlay)).call(this, props));

    _this.state = {
      corners: [L.latlng, L.latlng, L.latlng, L.latlng]
    };

    _this.state = {
      corners: props.corners
    };
    return _this;
  }

  createClass(ReactDistortableImageOverlay, [{
    key: 'onUpdate',
    value: function onUpdate(corners) {

      // Prevents leaflet-distortableimage from firing as edits are taking place
      if (corners === undefined || corners[0] === undefined) return;

      if (this.props.onCornersUpdated !== undefined) {
        this.props.onCornersUpdated(corners);
      }

      if (this.props.onWellKnownTextUpdated !== undefined) {

        // WKT needs to close off the polygon, also switch the BR & BL 
        var swappedLatLngs = [corners[0], corners[1], corners[3], corners[2], corners[0]];
        var flattenedLatLngs = swappedLatLngs.map(function (x) {
          return x.lng + ' ' + x.lat;
        });
        this.props.onWellKnownTextUpdated('POLYGON((' + flattenedLatLngs.join(', ') + '))');
      }

      this.setState({
        corners: corners
      });
    }
  }, {
    key: 'render',
    value: function render() {

      return React.createElement(ReactDistortableImageOverlayMapLayer$1, {
        url: this.props.url,
        opacity: this.props.opacity,
        corners: this.state.corners,
        editMode: this.props.editMode,
        onUpdate: this.onUpdate.bind(this)
      });
    }
  }]);
  return ReactDistortableImageOverlay;
}(React.Component);

export default ReactDistortableImageOverlay;
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ../dist/index.es.js
// module id = 242
// module chunks = 0